import { useEffect, useMemo, useState } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  EditableCell,
  Collection,
  SearchField,
  Picker,
  PickerItem,
  ComboBox,
  ComboBoxItem,
  TextField,
  TextArea,
  ActionButton,
  Button,
  ButtonGroup,
  DialogTrigger,
  Popover,
  Text,
  Badge,
  LabeledValue,
} from "@react-spectrum/s2";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import EditIcon from "@react-spectrum/s2/icons/Edit";

import { S3Video } from "@app/components/S3Video";
import {
  useFootage,
  useFootageIndustries,
  useUpdateFootage,
} from "@app/hooks/footage";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const ALL_INDUSTRIES = "__all__";

const columns = [
  { name: "Preview", id: "preview", isRowHeader: true, minWidth: 320 },
  { name: "Description", id: "description" },
  { name: "Details", id: "details", minWidth: 360 },
];

const EditableListFields = ({ "aria-label": ariaLabel, value, onChange }) => (
  <div className={style({ display: "flex", flexDirection: "column", gap: 8 })}>
    {value.map((item, index) => (
      <div
        key={index}
        className={style({ display: "flex", gap: 4, alignItems: "center" })}
      >
        <TextField
          autoFocus={index === 0}
          aria-label={`${ariaLabel} ${index + 1}`}
          value={item}
          onChange={(newValue) =>
            onChange(value.map((v, i) => (i === index ? newValue : v)))
          }
        />
        <ActionButton
          aria-label={`Remove ${ariaLabel} ${index + 1}`}
          isQuiet
          onPress={() => onChange(value.filter((_, i) => i !== index))}
        >
          <DeleteIcon />
        </ActionButton>
      </div>
    ))}
    <Button variant="secondary" onPress={() => onChange([...value, ""])}>
      <Text>Add {ariaLabel.toLowerCase()}</Text>
    </Button>
  </div>
);

const EditableIndustryValue = ({ item, options, isSaving, onSave }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <LabeledValue
      label="Industry"
      // size="S"
      contextualHelp={
        <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
          <ActionButton
            isQuiet
            isPending={isSaving}
            aria-label="Edit industry"
            size="XS"
          >
            <EditIcon />
          </ActionButton>
          <Popover size="S">
            <form
              className={style({
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: 12,
                minWidth: 200,
              })}
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const value = formData.get("industry")?.trim();
                setIsOpen(false);
                if (value) {
                  onSave(value);
                }
              }}
            >
              <ComboBox
                autoFocus
                label="Industry"
                name="industry"
                items={options}
                defaultInputValue={item.industry}
                allowsCustomValue
                menuTrigger="focus"
              >
                {(option) => (
                  <ComboBoxItem id={option.id} textValue={option.label}>
                    {option.label}
                  </ComboBoxItem>
                )}
              </ComboBox>
              <ButtonGroup>
                <Button variant="accent" type="submit">
                  <Text>Save</Text>
                </Button>
              </ButtonGroup>
            </form>
          </Popover>
        </DialogTrigger>
      }
      value={<Badge>{item.industry || "—"}</Badge>}
    />
  );
};

const EditableListValue = ({ label, values, isSaving, onSave }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(values);

  return (
    <LabeledValue
      label={label}
      // size="S"
      contextualHelp={
        <DialogTrigger
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (open) {
              setDraft(values);
            }
          }}
        >
          <ActionButton
            isQuiet
            isPending={isSaving}
            aria-label={`Edit ${label.toLowerCase()}`}
            size="XS"
          >
            <EditIcon />
          </ActionButton>
          <Popover size="S">
            <form
              className={style({
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: 12,
                minWidth: 280,
              })}
              onSubmit={(e) => {
                e.preventDefault();
                setIsOpen(false);
                onSave(draft.map((v) => v.trim()).filter(Boolean));
              }}
            >
              <EditableListFields
                aria-label={label}
                value={draft}
                onChange={setDraft}
              />
              <ButtonGroup>
                <Button variant="accent" type="submit">
                  <Text>Save</Text>
                </Button>
              </ButtonGroup>
            </form>
          </Popover>
        </DialogTrigger>
      }
      value={
        <div className={style({ display: "flex", gap: 4, flexWrap: "wrap" })}>
          {values.length ? (
            values.map((value) => (
              <Badge key={value} fillStyle="outline">
                {value}
              </Badge>
            ))
          ) : (
            <Text>—</Text>
          )}
        </div>
      }
    />
  );
};

export const AdminFootage = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "Footage" }]);

  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [debouncedFilters, setDebouncedFilters] = useState({
    q: "",
    industry: "",
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedFilters({ q: search, industry });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [search, industry]);

  const filters = useMemo(
    () => ({
      ...(debouncedFilters.q && { q: debouncedFilters.q }),
      ...(debouncedFilters.industry && { industry: debouncedFilters.industry }),
      pageSize: PAGE_SIZE,
    }),
    [debouncedFilters],
  );

  const { data: industries = [] } = useFootageIndustries();

  const industryItems = useMemo(
    () => [
      { id: ALL_INDUSTRIES, label: "All industries" },
      ...industries.map((name) => ({ id: name, label: name })),
    ],
    [industries],
  );

  const industryOptions = useMemo(
    () => industries.map((name) => ({ id: name, label: name })),
    [industries],
  );

  const updateFootageMutation = useUpdateFootage();

  const isSavingField = (item, field) =>
    updateFootageMutation.isPending &&
    updateFootageMutation.variables?.id === item._id &&
    field in updateFootageMutation.variables;

  const submitField = (item, field) => (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateFootageMutation.mutate({
      id: item._id,
      [field]: formData.get(field),
    });
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
  } = useFootage(filters);

  const rows = data?.pages?.flatMap((page) => page.footage) ?? [];
  const total = data?.pages?.at(-1)?.pagination?.total ?? 0;

  const loadingState = isError
    ? "error"
    : isLoading
      ? "loading"
      : isFetchingNextPage
        ? "loadingMore"
        : "idle";

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        size: "full",
        maxHeight: "calc(100dvh - 56px - 48px)",
      })}
    >
      <title>Footage | BeStory</title>
      <div className={style({ font: "heading" })}>Footage</div>

      <div className={style({ display: "flex", gap: 12, flexWrap: "wrap" })}>
        <SearchField
          aria-label="Search footage"
          placeholder="Search description, key elements, attributes"
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
          styles={style({ width: 320 })}
        />
        <Picker
          aria-label="Filter by industry"
          items={industryItems}
          value={industry || ALL_INDUSTRIES}
          onChange={(key) => setIndustry(key === ALL_INDUSTRIES ? "" : key)}
          styles={style({ width: 220 })}
        >
          {(item) => (
            <PickerItem id={item.id} textValue={item.label}>
              {item.label}
            </PickerItem>
          )}
        </Picker>
        <Text styles={style({ font: "body-sm", alignSelf: "center" })}>
          {total} total
        </Text>
      </div>

      <TableView
        aria-label="Footage"
        loadingState={loadingState}
        onLoadMore={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        renderEmptyState={() =>
          isError
            ? (error?.message ?? "Failed to load footage")
            : "No footage found"
        }
        overflowMode="wrap"
        density="spacious"
        styles={style({ maxHeight: "full", height: "100%" })}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              isRowHeader={column.isRowHeader}
              width={column.width}
              minWidth={column.minWidth}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody>
          <Collection items={rows}>
            {(item) => (
              <Row id={item.fileName}>
                <Cell>
                  <S3Video
                    filePath={item.fileName}
                    autoPlay={false}
                    controls
                    loading="lazy"
                    muted
                    loop
                    preload="metadata"
                    className={style({
                      display: "block",
                      width: "full",
                      aspectRatio: "16 / 9",
                      borderRadius: "default",
                      objectFit: "cover",
                    })}
                  />
                </Cell>
                <EditableCell
                  isSaving={isSavingField(item, "description")}
                  onSubmit={submitField(item, "description")}
                  renderEditing={() => (
                    <TextArea
                      autoFocus
                      aria-label="Description"
                      name="description"
                      defaultValue={item.description}
                      styles={style({ width: "full", minWidth: 640 })}
                    />
                  )}
                >
                  <div
                    className={style({
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      justifyContent: "space-between",
                    })}
                  >
                    <p className={style({ font: "body-sm" })}>
                      {item.description}
                    </p>
                    <ActionButton
                      slot="edit"
                      aria-label="Edit description"
                      size="S"
                    >
                      <EditIcon />
                    </ActionButton>
                  </div>
                </EditableCell>
                <Cell>
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      paddingY: 8,
                    })}
                  >
                    <EditableIndustryValue
                      item={item}
                      options={industryOptions}
                      isSaving={isSavingField(item, "industry")}
                      onSave={(value) =>
                        updateFootageMutation.mutate({
                          id: item._id,
                          industry: value,
                        })
                      }
                    />
                    <EditableListValue
                      label="Key elements"
                      values={item.keyElements ?? []}
                      isSaving={isSavingField(item, "keyElements")}
                      onSave={(value) =>
                        updateFootageMutation.mutate({
                          id: item._id,
                          keyElements: value,
                        })
                      }
                    />
                    <EditableListValue
                      label="Attributes"
                      values={item.attributes ?? []}
                      isSaving={isSavingField(item, "attributes")}
                      onSave={(value) =>
                        updateFootageMutation.mutate({
                          id: item._id,
                          attributes: value,
                        })
                      }
                    />
                    {!!item.productContext && (
                      <LabeledValue
                        label="Product context"
                        value={
                          <Text
                            styles={style({
                              font: "code-xs",
                              whiteSpace: "pre-wrap",
                            })}
                          >
                            {JSON.stringify(item.productContext, null, 2)}
                          </Text>
                        }
                      />
                    )}
                  </div>
                </Cell>
              </Row>
            )}
          </Collection>
        </TableBody>
      </TableView>
    </div>
  );
};
